import { Badge, Button, Input, Modal, Select, Textarea } from '@/components/ui';
import { useCrearRequisito, useNtcsDisponibles } from '@/hooks/useSysadmin';
import type { CrearRequisitoRequest } from '@/types/types';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

interface CrearRequisitoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CrearRequisitoModal: React.FC<CrearRequisitoModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { ntcs, isLoading: ntcsLoading } = useNtcsDisponibles();
  const { isLoading: creando, error: crearError, crearRequisito } = useCrearRequisito();

  const [formData, setFormData] = useState<CrearRequisitoRequest>({
    capitulo: '',
    descripcion: '',
    numeral: '',
    ntcId: 0,
    posibleEvidencia: '',
  });

  const [validation, setValidation] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [ntcLocked, setNtcLocked] = useState(false);

  // Reset form cuando se abre/cierra
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        capitulo: '',
        descripcion: '',
        numeral: '',
        ntcId: 0,
        posibleEvidencia: '',
      });
      setValidation({});
      setSuccessMessage('');
      setNtcLocked(false);
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.capitulo.trim()) errors.capitulo = 'Capítulo es requerido';
    if (!formData.numeral.trim()) errors.numeral = 'Numeral es requerido';
    if (!formData.descripcion.trim()) errors.descripcion = 'Descripción es requerida';
    if (!formData.ntcId) errors.ntcId = 'NTC es requerido';

    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNtcChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const ntcId = parseInt(e.target.value, 10);
    setFormData((prev) => ({ ...prev, ntcId }));
    setNtcLocked(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSuccessMessage('');
      const response = await crearRequisito(formData);

      if (response.success) {
        setSuccessMessage(`✓ Requisito creado exitosamente. Asociado a ${response.requisito?.empresasPropagadas || 0} empresa(s).`);
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 2000);
      }
    } catch (err) {
      console.error('Error al crear requisito:', err);
      // El error ya está en el estado `crearError`
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Crear Nuevo Requisito" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Mensaje de éxito */}
        {successMessage && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Mensaje de error */}
        {(crearError || Object.keys(validation).length > 0) && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              {crearError && <p>{crearError}</p>}
              {Object.keys(validation).length > 0 && (
                <ul className="list-disc list-inside mt-2">
                  {Object.entries(validation).map(([field, message]) => (
                    <li key={field}>{message}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Campo: Capítulo */}
        <div>
          <label htmlFor="capitulo" className="block text-sm font-medium text-gray-700 mb-2">
            Capítulo <span className="text-red-500">*</span>
          </label>
          <Input
            id="capitulo"
            type="text"
            placeholder="ej: 4"
            value={formData.capitulo}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, capitulo: e.target.value }));
              // Limpiar error de validación
              setValidation((prev) => {
                const copy = { ...prev };
                delete copy.capitulo;
                return copy;
              });
            }}
            disabled={creando}
          />
        </div>

        {/* Campo: Numeral */}
        <div>
          <label htmlFor="numeral" className="block text-sm font-medium text-gray-700 mb-2">
            Numeral <span className="text-red-500">*</span>
          </label>
          <Input
            id="numeral"
            type="text"
            placeholder="ej: 4.1"
            value={formData.numeral}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, numeral: e.target.value }));
              setValidation((prev) => {
                const copy = { ...prev };
                delete copy.numeral;
                return copy;
              });
            }}
            disabled={creando}
          />
        </div>

        {/* Campo: Descripción */}
        <div>
          <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-2">
            Descripción <span className="text-red-500">*</span>
          </label>
          <Textarea
            id="descripcion"
            placeholder="Describe el requisito detalladamente..."
            value={formData.descripcion}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, descripcion: e.target.value }));
              setValidation((prev) => {
                const copy = { ...prev };
                delete copy.descripcion;
                return copy;
              });
            }}
            rows={3}
            disabled={creando}
          />
        </div>

        {/* Campo: NTC (Bloqueado después de seleccionar) */}
        <div>
          <label htmlFor="ntc" className="block text-sm font-medium text-gray-700 mb-2">
            NTC <span className="text-red-500">*</span>
            {ntcLocked && (
              <Badge variant="blue" className="ml-2">
                Bloqueado
              </Badge>
            )}
          </label>
          <Select
            id="ntc"
            value={String(formData.ntcId || '')}
            onChange={handleNtcChange}
            disabled={creando || ntcLocked}
            className={ntcLocked ? 'opacity-60 cursor-not-allowed' : ''}
            options={
              ntcsLoading
                ? [{ value: '', label: 'Cargando NTCs...' }]
                : [
                    { value: '', label: '-- Selecciona una NTC --' },
                    ...ntcs.map((ntc) => ({
                      value: String(ntc.ntcId),
                      label: `${ntc.nombre} (${ntc.totalRequisitos} requisitos)`,
                    })),
                  ]
            }
          />
        </div>

        {/* Campo: Posible Evidencia (Opcional) */}
        <div>
          <label htmlFor="posibleEvidencia" className="block text-sm font-medium text-gray-700 mb-2">
            Posible Evidencia <span className="text-gray-500">(Opcional)</span>
          </label>
          <Input
            id="posibleEvidencia"
            type="text"
            placeholder="ej: Documento de análisis, Certificado, etc."
            value={formData.posibleEvidencia}
            onChange={(e) => setFormData((prev) => ({ ...prev, posibleEvidencia: e.target.value }))}
            disabled={creando}
          />
        </div>

        {/* Botones de acción */}
        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onClose} disabled={creando}>
            Cancelar
          </Button>
          <Button type="submit" disabled={creando || successMessage !== ''}>
            {creando ? 'Creando...' : 'Crear Requisito'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
